import React, { useEffect, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { userService } from '@/shared/services/userService';

// The only field the profile routes name in a refusal's `field`; its form
// control has the same id.
const FORM_FIELD = 'studentId';

function CreateProfile() {
    const {
        currentUser,
        refreshProfile,
        getMicrosoftAccessToken,
        clearMicrosoftAccessToken,
    } = useAuth();
    // A federated sign-in (Microsoft) supplies a display name; an email and
    // password account has none. Either way the field stays editable.
    const [formData, setFormData] = useState({
        name: currentUser?.displayName || '',
        department: '',
        major: '',
        studentId: '',
        phone: '',
    });
    const [error, setError] = useState('');
    const [fieldError, setFieldError] = useState(null);
    const [loading, setLoading] = useState(false);
    // The account type is never self-declared. The server returns a directory
    // label only for a verified faculty affiliation, and the server grants
    // faculty on its own re-check of the same directory entry; only a
    // Microsoft sign-in can have one. Anything that goes wrong asking leaves
    // this null, and the profile is created as a student with no error shown.
    const [directory, setDirectory] = useState(null);
    // Submitting before the check answers would create a faculty member as a
    // student, so the form waits for it.
    const [checkingDirectory, setCheckingDirectory] = useState(() =>
        Boolean(getMicrosoftAccessToken())
    );

    const navigate = useNavigate();

    useEffect(() => {
        const token = getMicrosoftAccessToken();
        if (!token) return undefined;
        let cancelled = false;
        userService
            .getFacultyEligibility(token)
            .then((result) => {
                if (cancelled) return;
                const title =
                    result?.eligible === true &&
                    typeof result.jobTitle === 'string' &&
                    result.jobTitle.trim();
                if (!title) {
                    setDirectory(null);
                    return;
                }
                const department =
                    (typeof result.department === 'string' &&
                        result.department.trim()) ||
                    null;
                setDirectory({ title, department });
                // A prefill only: anything already typed is kept.
                if (department) {
                    setFormData((prev) =>
                        prev.department.trim() ? prev : { ...prev, department }
                    );
                }
            })
            .catch(() => {
                if (!cancelled) setDirectory(null);
            })
            .finally(() => {
                if (!cancelled) setCheckingDirectory(false);
            });
        return () => {
            cancelled = true;
        };
    }, [getMicrosoftAccessToken]);

    useEffect(() => {
        if (fieldError) document.getElementById(fieldError.field)?.focus();
    }, [fieldError]);

    const isFaculty = Boolean(directory);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setFieldError((prev) => (prev?.field === name ? null : prev));
    };

    async function handleSubmit(e) {
        e.preventDefault();

        if (!formData.name.trim()) {
            return setError('Name is required');
        }

        try {
            setError('');
            setFieldError(null);
            setLoading(true);

            const profileData = {
                name: formData.name.trim(),
                phone: formData.phone.trim() || undefined,
            };

            // The server re-reads the directory with the token and grants
            // faculty on that reading alone, so the token goes with every
            // Microsoft sign-up, not only one the form saw as eligible: a
            // directory check that failed or timed out here must not decide
            // the outcome. Faculty have no student ID, so none is sent for
            // them.
            const microsoftAccessToken = getMicrosoftAccessToken();
            if (microsoftAccessToken) {
                profileData.microsoftAccessToken = microsoftAccessToken;
            }
            if (isFaculty) {
                profileData.department =
                    formData.department.trim() || undefined;
            } else {
                profileData.major = formData.major.trim() || undefined;
                if (formData.studentId.trim()) {
                    profileData.studentId = formData.studentId.trim();
                }
            }

            await userService.createProfile(profileData);
            clearMicrosoftAccessToken();
            refreshProfile();
            navigate('/dashboard');
        } catch (err) {
            const { field, message } = err.response?.data || {};
            if (field === FORM_FIELD && message) {
                setFieldError({ field, message });
            } else if (err.response?.status === 409) {
                setError('A profile already exists for this account.');
            } else {
                setError('Failed to create profile. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col md={8} lg={6}>
                    <Card>
                        <Card.Body className="p-4">
                            <h2 className="text-center mb-2">
                                Complete Your Profile
                            </h2>
                            {currentUser?.email && (
                                <p className="text-muted text-center mb-4">
                                    Signed in as {currentUser.email}
                                </p>
                            )}

                            {error && <Alert variant="danger">{error}</Alert>}

                            {/* A live region that stays mounted, so the note is
                                announced when it appears after the directory
                                check. It is not role="status": the directory
                                check's own status region is the one status. */}
                            <div aria-live="polite">
                                {directory && (
                                    <Alert
                                        variant="info"
                                        transition={false}
                                        role="note"
                                    >
                                        The university directory lists you as{' '}
                                        <strong>{directory.title}</strong>
                                        {directory.department ? (
                                            <>
                                                {' '}
                                                in{' '}
                                                <strong>
                                                    {directory.department}
                                                </strong>
                                            </>
                                        ) : null}
                                        . Your account will have faculty access,
                                        so you can request peer support for your
                                        courses.
                                    </Alert>
                                )}
                            </div>

                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Full Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter your full name"
                                        required
                                    />
                                </Form.Group>

                                {!isFaculty && (
                                    <Form.Group
                                        className="mb-3"
                                        controlId="studentId"
                                    >
                                        <Form.Label>
                                            Student ID (900 number)
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="studentId"
                                            value={formData.studentId}
                                            onChange={handleChange}
                                            placeholder="e.g. 900123456"
                                            isInvalid={
                                                fieldError?.field ===
                                                'studentId'
                                            }
                                            aria-invalid={
                                                fieldError?.field ===
                                                'studentId'
                                            }
                                            aria-describedby={
                                                fieldError?.field ===
                                                'studentId'
                                                    ? 'studentId-error'
                                                    : undefined
                                            }
                                        />
                                        <Form.Control.Feedback
                                            type="invalid"
                                            id="studentId-error"
                                        >
                                            {fieldError?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                )}

                                {isFaculty ? (
                                    <Form.Group
                                        className="mb-3"
                                        controlId="department"
                                    >
                                        <Form.Label>Department</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="department"
                                            value={formData.department}
                                            onChange={handleChange}
                                            placeholder="e.g. Mathematics"
                                        />
                                    </Form.Group>
                                ) : (
                                    <Form.Group
                                        className="mb-3"
                                        controlId="major"
                                    >
                                        <Form.Label>Major</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="major"
                                            value={formData.major}
                                            onChange={handleChange}
                                            placeholder="e.g. Computer Science"
                                        />
                                    </Form.Group>
                                )}

                                <Form.Group className="mb-3" controlId="phone">
                                    <Form.Label>Phone (optional)</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="e.g. 303-555-0100"
                                    />
                                </Form.Group>

                                {/* The live region stays mounted and only its text
                                    changes: screen readers announce a change to an
                                    existing region, not one that appears or goes. */}
                                <p
                                    id="directory-check-status"
                                    className={
                                        checkingDirectory
                                            ? 'text-muted small mb-2'
                                            : 'visually-hidden'
                                    }
                                    role="status"
                                >
                                    {checkingDirectory
                                        ? 'Checking the university directory...'
                                        : ''}
                                </p>

                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-100 mt-2"
                                    disabled={loading || checkingDirectory}
                                    aria-busy={checkingDirectory}
                                    aria-describedby={
                                        checkingDirectory
                                            ? 'directory-check-status'
                                            : undefined
                                    }
                                >
                                    {loading
                                        ? 'Creating Profile...'
                                        : 'Complete Profile'}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default CreateProfile;
